import React from "react";
import type { Analyse } from "../../types/analyse";
import logoMimap from "../../assets/Logo MIMAP Horizontal second--02.png";
import { CLINIC_INFO } from "../../utils/preEmploymentUtils";

interface AnalysesContentProps {
  analyses: Analyse[];
  patientInfo: {
    nom: string;
    prenom: string;
    dateNaissance: string;
    adresse?: string;
  };
  doctorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    specialty?: string; // ✅ Ajouter la spécialité du médecin
  };
  consultationDate: string;
  instructions?: string; // ✅ Ajouter les instructions
}

const AnalysesContent: React.FC<AnalysesContentProps> = ({
  analyses,
  patientInfo,
  doctorInfo,
  consultationDate,
  instructions // ✅ Ajouter le paramètre instructions
}) => {
  const containerStyle: React.CSSProperties = {
    width: "128mm",
    maxWidth: "none",
    height: "auto",
    padding: "20px", // Réduit de 25px à 20px
    margin: "0 auto",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica, Arial, sans-serif",
    fontSize: "10px",
    lineHeight: "1.2", // Réduit de 1.4 à 1.2
    color: "#1f2937",
    position: "relative",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px", // Réduit de 20px à 12px
    paddingBottom: "8px", // Réduit de 12px à 8px
    borderBottom: "2px solid #113285",
  };

  const logoStyle: React.CSSProperties = {
    width: "150px", // Réduit de 180px à 150px
    height: "50px", // Réduit de 60px à 50px
    objectFit: "contain" as const,
  };

  const headerTextStyle: React.CSSProperties = {
    textAlign: "right",
    flex: "1",
    paddingLeft: "15px", // Réduit de 20px à 15px
  };

  const taglineStyle: React.CSSProperties = {
    fontSize: "8px", // Réduit de 9px à 8px
    fontWeight: "normal",
    color: "#113285",
    fontStyle: "italic",
  };

  // Titre du document centré - COMPACT
  const documentTitleStyle: React.CSSProperties = {
    textAlign: "center",
    marginBottom: "10px", // Réduit de 15px à 10px
    padding: "6px", // Réduit de 8px à 6px
    backgroundColor: "#f8f9ff",
    borderRadius: "4px",
  };

  const documentTitleTextStyle: React.CSSProperties = {
    fontSize: "12px", // Réduit de 13px à 12px
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "1px", // Réduit de 2px à 1px
  };

  const documentNumberStyle: React.CSSProperties = {
    fontSize: "8px", // Réduit de 9px à 8px
    color: "#6b7280",
    fontWeight: "normal",
  };

  const documentDateStyle: React.CSSProperties = {
    fontSize: "8px", // Réduit de 9px à 8px
    color: "#6b7280",
    fontWeight: "normal",
  };

  // Section patient épurée - COMPACT
  const patientSectionStyle: React.CSSProperties = {
    backgroundColor: "#fafbff",
    padding: "8px", // Réduit de 12px à 8px
    marginBottom: "10px", // Réduit de 15px à 10px
    borderRadius: "4px", // Réduit de 6px à 4px
    borderLeft: "3px solid #113285",
  };

  const patientTitleStyle: React.CSSProperties = {
    fontSize: "10px", // Réduit de 11px à 10px
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "6px", // Réduit de 8px à 6px
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const patientGridStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
  };

  const patientItemStyle: React.CSSProperties = {
    width: "48%",
    marginBottom: "3px", // Réduit de 4px à 3px
  };
  
  const patientLabelStyle: React.CSSProperties = {
    fontSize: "7px", // Réduit de 8px à 7px
    color: "#6b7280",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  };

  const patientValueStyle: React.CSSProperties = {
    fontSize: "9px", // Réduit de 10px à 9px
    color: "#111827",
    fontWeight: "normal",
    marginTop: "0.5px", // Réduit de 1px à 0.5px
  };

  // Section analyses épurée - COMPACT
  const analysesSectionStyle: React.CSSProperties = {
    marginBottom: "15px", // Réduit de 20px à 15px
  };

  const analysesTitleStyle: React.CSSProperties = {
    fontSize: "10px", // Réduit de 11px à 10px
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "8px", // Réduit de 10px à 8px
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: "3px", // Réduit de 4px à 3px
  };

  const analysesListStyle: React.CSSProperties = {
    paddingLeft: "0",
  };

  const analyseItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "5px", // Réduit de 8px à 5px
    paddingTop: "3px", // Réduit de 6px à 3px
    paddingBottom: "3px", // Réduit de 6px à 3px
  };

  const analyseNumberStyle: React.CSSProperties = {
    width: "18px", // Réduit de 20px à 18px
    fontSize: "8px", // Réduit de 9px à 8px
    fontWeight: "bold",
    color: "#113285",
    textAlign: "right",
    marginRight: "8px", // Réduit de 10px à 8px
  };

  const analyseContentStyle: React.CSSProperties = {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  };

  const analyseNameStyle: React.CSSProperties = {
    fontSize: "9px", // Réduit de 10px à 9px
    color: "#111827",
    fontWeight: "normal",
    marginBottom: "2px", // Réduit de 4px à 2px
    lineHeight: "1.3", // Réduit de 1.5 à 1.3
  };

  const analyseReasonStyle: React.CSSProperties = {
    fontSize: "8px", // Réduit de 9px à 8px
    color: "#6b7280",
    fontStyle: "italic",
  };

  // ✅ Styles pour la section instructions
  const instructionsSectionStyle: React.CSSProperties = {
    backgroundColor: "#fef3c7",
    padding: "8px",
    marginBottom: "15px",
    borderRadius: "4px",
    borderLeft: "3px solid #f59e0b",
  };

  const instructionsTitleStyle: React.CSSProperties = {
    fontSize: "10px",
    fontWeight: "bold",
    color: "#92400e",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const instructionsTextStyle: React.CSSProperties = {
    fontSize: "9px",
    color: "#451a03",
    lineHeight: "1.3",
  };

  const footerStyle: React.CSSProperties = {
    marginTop: "15px", // Réduit de 20px à 15px
    paddingTop: "10px", // Réduit de 12px à 10px
    borderTop: "1px solid #e5e7eb",
    textAlign: "center",
  };

  const footerTextStyle: React.CSSProperties = {
    fontSize: "7px", // Réduit de 8px à 7px
    color: "#6b7280",
    marginBottom: "1px", // Réduit de 2px à 1px
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const generateAnalysesNumber = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const r = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `AN-${y}${m}${day}-${r}`;
  };

  return (
    <div className="analyses-content" style={containerStyle}>
      <style>
        {`
          @media print {
            .analyses-content {
              width: 128mm !important;
              padding: 20px !important;
              margin: 0 !important;
              box-shadow: none !important;
            }
            body { margin: 0; padding: 0; }
            .print-hidden { display: none !important; }
            @page { size: A5; margin: 8mm; }
          }
        `}
      </style>
      
      {/* En-tête épuré - COMPACT */}
      <div style={headerStyle}>
        <img src={logoMimap} alt="Logo MIMAP" style={logoStyle} />
        <div style={headerTextStyle}>
          <div style={taglineStyle}>Votre santé, notre priorité</div>
        </div>
      </div>

      {/* Titre du document centré - COMPACT */}
      <div style={documentTitleStyle}>
        <div style={documentTitleTextStyle}>
          DEMANDE D'ANALYSES COMPLÉMENTAIRES
        </div>
        <div style={documentNumberStyle}>
          N° {generateAnalysesNumber()}
        </div>
        <div style={documentDateStyle}>
          Émise le {formatDate(consultationDate)}
        </div>
      </div>

      {/* Informations patient dans un bloc épuré - COMPACT */}
      <div style={patientSectionStyle}>
        <div style={patientTitleStyle}>Informations Patient</div>
        <div style={patientGridStyle}>
          <div style={patientItemStyle}>
            <div style={patientLabelStyle}>Nom complet</div>
            <div style={patientValueStyle}>
              {patientInfo.nom} {patientInfo.prenom}
            </div>
          </div>
          <div style={patientItemStyle}>
            <div style={patientLabelStyle}>Date de naissance</div>
            <div style={patientValueStyle}>
              {formatDate(patientInfo.dateNaissance)}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des analyses avec nom et raison - COMPACT */}
      <div style={analysesSectionStyle}>
        <div style={analysesTitleStyle}>Analyses demandées</div>
        <div style={analysesListStyle}>
          {analyses.map((analyse, index) => (
            <div key={`${analyse.id || index}`} style={analyseItemStyle}>
              <div style={analyseNumberStyle}>{index + 1}.</div>
              <div style={analyseContentStyle}>
                <div style={analyseNameStyle}>
                  {analyse.label || analyse.nom}
                </div>
                {analyse.reason && (
                  <div style={analyseReasonStyle}>
                    * {analyse.reason}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Section Instructions - si présentes */}
      {instructions && (
        <div style={instructionsSectionStyle}>
          <div style={instructionsTitleStyle}>INSTRUCTIONS SPÉCIALES</div>
          <div style={instructionsTextStyle}>{instructions}</div>
        </div>
      )}

      {/* Signature et cachet - même disposition que l'ordonnance */}
      <div style={{ marginTop: "40px", display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: "50%", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div style={{ marginBottom: "40px", textAlign: "right" }}>
            <div style={{ fontSize: "9px", color: "#4a5568" }}>Signature et cachet du médecin</div>
          </div>

          <div style={{ marginTop: "0", textAlign: "right", fontSize: "10px", color: "#1f2937" }}>
            <div style={{ fontWeight: "bold", fontSize: "10px", color: "#113285" }}>
              {doctorInfo.title} {doctorInfo.firstName} {doctorInfo.lastName}
            </div>
            <div style={{ fontSize: "9px", color: "#4b5563" }}>
              {doctorInfo.specialty || "Médecin Généraliste"}
            </div>
            <div style={{ marginTop: "5px", fontSize: "8px", color: "#6b7280" }}>
              Fait le{" "}
              {new Date(consultationDate).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Pied de page épuré - COMPACT */}
      <div style={footerStyle}>
        <div style={footerTextStyle}>{CLINIC_INFO.address}</div>
        <div style={footerTextStyle}>
          Tél: {CLINIC_INFO.phone} • Email: {CLINIC_INFO.email}
        </div>
      </div>
    </div>
  );
};

export default AnalysesContent;